import '@modules/workspaces/host'

import { WorkspaceService } from '@graft/nuget-gc.webportals.workspaces'

import { getSessionToken } from '@common/helpers'
import { detectUserRegion } from '@common/utils/detectUserRegion'

import { InviteSendOutcomeKind } from '@modules/invitation/invitation.const'
import type { InvitationOperationResult } from '@modules/invitation/invitation.interface'
import { resolveInviteSendOutcome } from '@modules/invitation/utils/resolveInviteSendOutcome'
import {
  deleteWorkspaceMembership,
  inviteProjectMember as inviteProjectMemberApi,
  inviteWorkspaceMember as inviteWorkspaceMemberApi,
} from '@modules/memberships/memberships'
import { buildPendingMember, normalizeInviteEmail } from '@modules/projects/project-members.utils'
import { getProjectMembers } from '@modules/projects/projects'
import { useProjectsStore } from '@modules/projects/stores/projects.store'
import { useUserStore } from '@modules/user/stores/user.store'
import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'
import { leaveWorkspaceMembershipAndReloadClientData } from '@modules/workspaces/stores/workspaces-membership-reload.thunks'

import type { WorkspaceMemberRole } from '../../settings.interface'
import { assertNoInviteEmailConflict } from '../../utils/assertNoInviteEmailConflict'
import { InviteEmailConflictError } from '../../utils/InviteEmailConflictError'
import { InviteMemberFlowError } from '../../utils/InviteMemberFlowError'
import { isCurrentWorkspaceMember } from '../../utils/isCurrentWorkspaceMember'
import { workspaceMemberToObject } from '../objects'

import { useWorkspaceStore } from './workspace.store'
import type { InviteMemberCallbacks } from './workspace.type'

const handleInviteError = (err: unknown, callbacks?: InviteMemberCallbacks) => {
  if (err instanceof InviteEmailConflictError) {
    callbacks?.onError?.(err)
    return
  }

  const errorMessage = err instanceof Error ? err.message : 'Failed to invite member'
  useWorkspaceStore.getState().setState({ error: errorMessage })
  callbacks?.onError?.(InviteMemberFlowError.stayOpen(errorMessage))
}

const dispatchInviteSendOutcome = (
  result: InvitationOperationResult,
  callbacks?: InviteMemberCallbacks,
  onSuccess?: () => void
) => {
  const { kind, message } = resolveInviteSendOutcome(result)

  if (kind === InviteSendOutcomeKind.Success) {
    onSuccess?.()
    callbacks?.onSuccess?.()
    return
  }

  if (kind === InviteSendOutcomeKind.Informational) {
    callbacks?.onInformational?.(message)
    return
  }

  if (kind === InviteSendOutcomeKind.Conflict) {
    callbacks?.onError?.(InviteMemberFlowError.stayOpen(message))
    return
  }

  callbacks?.onError?.(InviteMemberFlowError.closeAfterToast(message))
}

export async function getWorkspaceMembers(): Promise<void> {
  const { workspace, setState, updateState, finishRequest } = useWorkspaceStore.getState()
  if (!workspace) return

  setState({ loading: true, error: null, members: [] })

  try {
    const token = await getSessionToken()
    const members = await WorkspaceService.getWorkspaceMembers(token, workspace.id)
    const result = await Promise.all((members ?? []).map(workspaceMemberToObject))

    updateState((state) => {
      state.members = result
      if (state.workspace) {
        state.workspace.memberCount = result.length
      }
    })
  } catch (err) {
    updateState((state) => {
      state.members = []
      state.error = err instanceof Error ? err.message : 'Failed to load workspace members'
      if (state.workspace) {
        state.workspace.memberCount = 0
      }
    })
  } finally {
    finishRequest()
  }
}

async function ensureWorkspaceMembersLoaded(): Promise<void> {
  if (useWorkspaceStore.getState().members.length > 0) return

  await getWorkspaceMembers()
}

export async function inviteWorkspaceMember(
  email: string,
  role: WorkspaceMemberRole,
  callbacks?: InviteMemberCallbacks
): Promise<void> {
  useWorkspaceStore.getState().startRequest()

  try {
    const workspaceId =
      useWorkspaceStore.getState().workspace?.id ?? useWorkspacesStore.getState().currentWorkspace?.workspaceId
    if (!workspaceId) {
      throw new Error('No current workspace')
    }

    await ensureWorkspaceMembersLoaded()
    assertNoInviteEmailConflict(useWorkspaceStore.getState().members, email, 'workspace')

    const result = await inviteWorkspaceMemberApi(workspaceId, role, email, detectUserRegion())
    dispatchInviteSendOutcome(result, callbacks)
  } catch (err) {
    handleInviteError(err, callbacks)
  } finally {
    useWorkspaceStore.getState().finishRequest()
  }
}

export async function inviteProjectMember(
  email: string,
  role: WorkspaceMemberRole,
  callbacks?: InviteMemberCallbacks
): Promise<void> {
  useWorkspaceStore.getState().startRequest()

  try {
    if (!useWorkspaceStore.getState().workspace) {
      throw new Error('No current workspace')
    }

    const currentProject = useProjectsStore.getState().currentProject
    if (!currentProject) {
      throw new Error('No current project selected')
    }

    const projectMembers = await getProjectMembers(currentProject.projectId)
    assertNoInviteEmailConflict(projectMembers, email, 'project')

    const result = await inviteProjectMemberApi(currentProject.projectId, role, email, detectUserRegion())

    dispatchInviteSendOutcome(result, callbacks, () => {
      const emailLower = normalizeInviteEmail(email)

      useWorkspaceStore.getState().updateState((state) => {
        if (!state.members.some((member) => normalizeInviteEmail(member.email) === emailLower)) {
          state.members.unshift(buildPendingMember(email, role, currentProject.projectId, 'workspace-member'))
        }
        if (state.workspace) {
          state.workspace.memberCount = state.members.length
        }
      })
    })
  } catch (err) {
    handleInviteError(err, callbacks)
  } finally {
    useWorkspaceStore.getState().finishRequest()
  }
}

export type RemoveMemberResult = {
  leftWorkspace: boolean
}

export async function removeMember(memberId: string): Promise<RemoveMemberResult> {
  const { workspace, members: previousMembers, startRequest, updateState, finishRequest } = useWorkspaceStore.getState()
  if (!workspace) {
    return { leftWorkspace: false }
  }

  startRequest()

  try {
    const member = previousMembers.find((item) => item.id === memberId)
    const currentUser = useUserStore.getState().currentUser
    if (member && isCurrentWorkspaceMember(member, currentUser)) {
      await leaveWorkspaceMembershipAndReloadClientData(workspace.id, member.id)
      return { leftWorkspace: true }
    }

    await deleteWorkspaceMembership(workspace.id, memberId)

    updateState((state) => {
      state.members = state.members.filter((item) => item.id !== memberId)
      if (state.workspace) {
        state.workspace.memberCount = state.members.length
      }
    })

    return { leftWorkspace: false }
  } catch (err) {
    updateState((state) => {
      state.members = previousMembers
      state.error = err instanceof Error ? err.message : 'Failed to remove member'
      if (state.workspace) {
        state.workspace.memberCount = previousMembers.length
      }
    })
    return { leftWorkspace: false }
  } finally {
    finishRequest()
  }
}

export async function updateMemberRole(memberId: string, role: WorkspaceMemberRole): Promise<void> {
  const { workspace, startRequest, updateState, setRequestError, finishRequest } = useWorkspaceStore.getState()
  if (!workspace) return

  startRequest()

  try {
    const token = await getSessionToken()
    await WorkspaceService.updateWorkspaceMemberLevel(token, workspace.id, memberId, role)

    updateState((state) => {
      const member = state.members.find((item) => item.id === memberId)
      if (member) {
        member.role = role
      }
    })
  } catch (err) {
    setRequestError(err, 'Failed to update member role')
  } finally {
    finishRequest()
  }
}
